import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import api from "@/lib/api";
import { ShoppingBag, Lock, Sparkles, Loader2, CheckCircle, X } from "lucide-react";
import { InfoCard } from "@/components/toggles";
import RichTextView from "@/components/common/RichTextView";
import PaymentChoiceModal from "@/components/PaymentChoiceModal";

// Original-URL → Blur-Preview-URL (Server-side /api/blur-Endpoint).
function toBlurUrl(url) {
  if (!url) return url;
  if (url.startsWith("/api/blur/")) return url;
  if (url.startsWith("/api/files/")) return url.replace("/api/files/", "/api/blur/");
  return `/api/blur/${url}`;
}

/**
 * ExclusiveTabContent — Abos + Einzelkauf-Medien eines Creators.
 * Wiederverwendet im öffentlichen Profil UND im eigenen Profil (Übersicht wie andere sie sehen).
 */
export default function ExclusiveTabContent({ profile, navigate, user }) {
  const { t } = useTranslation();
  const [tiers, setTiers] = useState(null);
  const [paidMedia, setPaidMedia] = useState([]);
  const [mySubs, setMySubs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);
  const [error, setError] = useState("");
  const [detailAsset, setDetailAsset] = useState(null);
  const [playingVideo, setPlayingVideo] = useState(false);
  useEffect(() => { setPlayingVideo(false); }, [detailAsset?.asset_id]);
  const isSubscribed = mySubs.some((s) => s.creator_id === profile.person_id && s.status === "ACTIVE");
  const activeTier = (mySubs.find((s) => s.creator_id === profile.person_id && s.status === "ACTIVE") || {}).tier;

  useEffect(() => {
    (async () => {
      try {
        const [tiersRes, mediaRes] = await Promise.allSettled([
          api.get(`/creators/${profile.handle}/subscription-tiers`),
          api.get(`/creators/${profile.handle}/paid-media`),
        ]);
        if (tiersRes.status === "fulfilled") setTiers(tiersRes.value.data || null);
        else setTiers(null);
        if (mediaRes.status === "fulfilled") setPaidMedia(mediaRes.value.data?.assets || []);
        if (user && user !== false) {
          try {
            const { data: sd } = await api.get("/me/subscriptions");
            setMySubs(sd.subscriptions || []);
          } catch { /* ignore */ }
        }
      } finally { setLoading(false); }
    })();
  }, [profile.handle, profile.person_id, user]);

  const [payChoice, setPayChoice] = useState(null);

  const subscribeTier = async (tierUpper) => {
    if (!user || user === false) { navigate("/login"); return; }
    setError("");
    const price = tiers?.[`${tierUpper.toLowerCase()}_price_eur`];
    setPayChoice({ kind: "tier", ref: tierUpper, amount: price, title: `${tierUpper} · Abo` });
  };
  const runCheckout = async (method) => {
    if (!payChoice) return {};
    const origin = window.location.origin;
    const url = payChoice.kind === "tier"
      ? `/creators/${profile.handle}/subscribe/${payChoice.ref}/checkout`
      : `/paid-media/${payChoice.ref}/checkout`;
    const { data } = await api.post(url, { origin_url: origin, payment_method: method });
    if (data.url) { window.location.href = data.url; return { redirect: true }; }
    if (data.mock && (data.session_id || data.tx_id)) {
      await api.post("/payments/mock-confirm", { session_id: data.session_id, tx_id: data.tx_id });
      return { done: true };
    }
    if (data.bank_instructions) return { bank_instructions: data.bank_instructions };
    return {};
  };

  const addAssetToCart = async (asset_id) => {
    if (!user || user === false) { navigate("/login"); return; }
    setError(""); setBusyId(asset_id);
    try {
      await api.post("/cart/paid-media", { asset_id });
      setDetailAsset(null);
      navigate("/cart");
    } catch (e) {
      const detail = e?.response?.data?.detail;
      const code = typeof detail === "object" ? detail?.code : null;
      if (code === "ALREADY_OWNED") setError(t("public_profile.already_owned"));
      else setError(typeof detail === "string" ? detail : t("public_profile.checkout_error"));
    } finally { setBusyId(null); }
  };

  if (loading) return <div className="flex justify-center py-8"><Loader2 size={20} className="animate-spin text-[#999]" /></div>;

  return (
    <div data-testid="profile-exklusiv" className="px-4 py-4 space-y-4">
      {error && <p data-testid="profile-exklusiv-error" className="text-xs text-red-500">{error}</p>}
      <PaymentChoiceModal
        open={!!payChoice}
        title={payChoice?.title}
        amount={payChoice?.amount}
        allowedMethods={payChoice?.kind === "tier" ? ["STRIPE", "PAYPAL"] : undefined}
        notice={payChoice?.kind === "tier"
          ? "Dieses Abonnement verlängert sich automatisch alle 30 Tage um weitere 30 Tage. Du kannst es jederzeit im Profil unter „Meine Käufe“ kündigen – es läuft dann noch bis zum Ende der bezahlten Laufzeit. Abos sind nur per Stripe oder PayPal zahlbar."
          : ""}
        onClose={() => { const wasDone = payChoice?._done; setPayChoice(null); if (wasDone) window.location.reload(); }}
        onConfirm={async (method) => {
          const r = await runCheckout(method);
          if (r?.done) setPayChoice((p) => (p ? { ...p, _done: true } : p));
          return r;
        }}
      />
      {isSubscribed && (
        <InfoCard variant="success" testId="profile-exklusiv-active">
          <CheckCircle size={14} /> {t("public_profile.active_sub", { name: profile.display_name })}
        </InfoCard>
      )}
      {tiers && tiers.published && Number(tiers.premium_price_eur) > 0 && (
        <section data-testid="profile-tiers-section">
          <h3 className="text-xs font-medium text-[#666] uppercase tracking-wider mb-2">{t("public_profile.abos", { defaultValue: "Premium-Abo" })}</h3>
          {(() => {
            const price = Number(tiers.premium_price_eur);
            const isActive = activeTier === "PREMIUM" || isSubscribed;
            const busyThis = busyId === "tier:PREMIUM";
            return (
              <div data-testid="profile-tier-premium" className="rounded-2xl border-2 border-brand-black bg-white p-5 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <div>
                  <p className="text-[10px] font-bold uppercase tracking-wider text-[#999]">Premium</p>
                  <p className="text-3xl font-black text-brand-black mt-1">
                    {price.toFixed(2)} €
                    <span className="text-xs font-normal text-[#666] ml-1">{t("public_profile.per_month", { defaultValue: "/ Monat" })}</span>
                  </p>
                  <p className="text-[11px] text-[#666] mt-1">{t("public_profile.monthly_recurring_hint")}</p>
                </div>
                <button
                  data-testid="profile-tier-subscribe-premium"
                  onClick={() => subscribeTier("PREMIUM")}
                  disabled={busyThis || isSubscribed}
                  className={`h-11 px-6 rounded-full text-sm font-bold flex items-center justify-center gap-2 disabled:opacity-40 transition-colors ${
                    isActive ? "bg-emerald-100 text-emerald-700 border border-emerald-300" : "bg-brand-black text-brand-white hover:bg-neutral-800"
                  }`}
                >
                  {busyThis ? <Loader2 size={14} className="animate-spin" /> : <Sparkles size={14} />}
                  {isActive ? t("public_profile.active", { defaultValue: "Aktiv" }) : t("public_profile.subscribe", { defaultValue: "Abonnieren" })}
                </button>
              </div>
            );
          })()}
        </section>
      )}
      {paidMedia.length > 0 && (
        <section>
          <h3 className="text-[10px] uppercase tracking-wider text-[#999] font-semibold mb-2">{t("public_profile.one_time")}</h3>
          <div className="grid gap-3" style={{ gridTemplateColumns: "repeat(auto-fill, minmax(200px, 1fr))" }}>
            {paidMedia.map((a) => (
              <div
                key={a.asset_id}
                data-testid={`profile-paid-${a.asset_id}`}
                role="button"
                tabIndex={0}
                onClick={() => setDetailAsset(a)}
                onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); setDetailAsset(a); } }}
                className="border border-[#E5E5E5] rounded-2xl bg-white overflow-hidden flex flex-col cursor-pointer hover:border-[#0A0A0A]/40 focus:outline-none focus:ring-2 focus:ring-[#0A0A0A]/20 transition-colors"
              >
                <div className="aspect-square bg-[#F5F5F5] flex items-center justify-center overflow-hidden relative">
                  {(a.owned || a.unlocked || isSubscribed) && (
                    <div data-testid={`profile-paid-owned-badge-${a.asset_id}`} className="absolute top-2 right-2 z-10 w-6 h-6 rounded-full bg-emerald-500 text-white flex items-center justify-center shadow-md">
                      <CheckCircle size={14} />
                    </div>
                  )}
                  {a.media_type === "IMAGE" && a.media_url ? (
                    <img src={a.preview_url || a.media_url} alt="" className={`w-full h-full object-cover ${(a.owned || a.unlocked || isSubscribed) ? "" : "blur-sm"}`} />
                  ) : a.media_type === "VIDEO" && a.preview_url && !/\.(mp4|mov|webm|m4v)(\?|$)/i.test(a.preview_url) ? (
                    <img src={a.preview_url} alt="" className="w-full h-full object-cover" />
                  ) : a.media_type === "VIDEO" ? (
                    <div data-testid={`profile-paid-video-fallback-${a.asset_id}`} className="w-full h-full bg-gradient-to-br from-[#111] to-[#333] flex items-center justify-center text-white">
                      <div className="w-12 h-12 rounded-full bg-white/25 border border-white/50 flex items-center justify-center backdrop-blur-md">
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="white"><path d="M8 5v14l11-7z" /></svg>
                      </div>
                    </div>
                  ) : (
                    <div className="text-xs text-[#999]">{t("public_profile.video_label")}{a.duration_sec ? " · " + Math.round(a.duration_sec/60) + " " + t("public_profile.minutes") : ""}</div>
                  )}
                  {!isSubscribed && !a.owned && !a.unlocked && (
                    <div className="absolute inset-0 flex flex-col items-center justify-center gap-1 bg-black/30 text-white">
                      <p className="text-[10px] uppercase tracking-wider font-bold">Einzelkauf</p>
                      <p data-testid={`profile-paid-medium-${a.asset_id}`} className="text-[10px] uppercase tracking-wider font-bold opacity-80">
                        {a.media_type === "VIDEO" ? "Video" : "Bild"}
                      </p>
                      <p className="text-xl font-bold mt-1">{a.price_eur.toFixed(2)} €</p>
                    </div>
                  )}
                </div>
                <div className="p-3 flex flex-col gap-2 flex-1">
                  <p className="text-sm font-semibold text-[#0A0A0A] line-clamp-1">{a.title}</p>
                  {a.description && (
                    <div className="text-xs text-[#666] line-clamp-3 flex-1">
                      <RichTextView html={a.description} />
                    </div>
                  )}
                  <div className="flex items-center justify-between gap-2 mt-1">
                    <p className="text-sm font-bold text-[#0A0A0A]">{a.price_eur.toFixed(2)} €</p>
                    {!isSubscribed && !a.owned && !a.unlocked ? (
                      <button
                        type="button"
                        data-testid={`profile-paid-addcart-${a.asset_id}`}
                        onClick={(e) => { e.stopPropagation(); addAssetToCart(a.asset_id); }}
                        disabled={busyId === a.asset_id}
                        className="rounded-full h-9 px-4 bg-[#0A0A0A] text-white text-xs font-medium disabled:opacity-40 hover:bg-[#222] flex items-center gap-1"
                      >
                        {busyId === a.asset_id ? <Loader2 size={12} className="animate-spin" /> : <ShoppingBag size={12} />}
                        {t("public_profile.add_to_cart", { defaultValue: "In den Warenkorb" })}
                      </button>
                    ) : (
                      <span data-testid={`profile-paid-owned-${a.asset_id}`} className="text-xs text-emerald-700 font-semibold">
                        {t("public_profile.owned_label", { defaultValue: "Bereits im Zugriff" })}
                      </span>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </section>
      )}
      {(!tiers || !(tiers.standard_price_eur || tiers.premium_price_eur || tiers.vip_price_eur)) && paidMedia.length === 0 && (
        <div className="text-center py-8">
          <Lock size={28} className="mx-auto text-[#CCC]" />
          <p className="mt-3 text-sm font-semibold text-[#0A0A0A]">{t("exclusive.title")}</p>
          <p className="mt-1 text-xs text-[#999]">{t("public_profile.no_exclusive_yet", { name: profile.display_name })}</p>
        </div>
      )}
      {detailAsset && (
        <div data-testid="profile-paid-detail-modal" className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4" onClick={() => setDetailAsset(null)}>
          <div className="bg-white rounded-2xl overflow-hidden w-full max-w-md max-h-[90vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between px-4 py-3 border-b border-[#E5E5E5]">
              <p className="text-sm font-bold text-[#0A0A0A] truncate">{detailAsset.title}</p>
              <button type="button" data-testid="profile-paid-detail-close" onClick={() => setDetailAsset(null)} className="h-8 w-8 rounded-full flex items-center justify-center hover:bg-[#F5F5F5]" aria-label={t("common.close", { defaultValue: "Schließen" })}>
                <X size={16} />
              </button>
            </div>
            <div className="overflow-y-auto flex-1">
              <div className="aspect-square bg-[#F5F5F5] flex items-center justify-center overflow-hidden">
                {detailAsset.media_type === "IMAGE" && detailAsset.media_url ? (
                  <img src={(detailAsset.owned || detailAsset.unlocked || isSubscribed) ? detailAsset.media_url : toBlurUrl(detailAsset.media_url)} alt="" className={`w-full h-full object-contain ${(detailAsset.owned || detailAsset.unlocked || isSubscribed) ? "" : "blur-sm"}`} />
                ) : detailAsset.media_type === "VIDEO" ? (
                  (playingVideo && detailAsset.play_url && (detailAsset.owned || detailAsset.unlocked || isSubscribed)) ? (
                    <video data-testid="profile-paid-detail-video-player" src={detailAsset.play_url} controls autoPlay playsInline className="w-full h-full object-contain bg-black" />
                  ) : (
                    <div data-testid="profile-paid-detail-video-poster" className="relative w-full h-full flex items-center justify-center bg-gradient-to-br from-[#111] to-[#333] text-white">
                      {detailAsset.preview_url && (<img src={detailAsset.preview_url} alt="" className="absolute inset-0 w-full h-full object-cover opacity-40 blur-sm" />)}
                      <div className="relative z-10 flex flex-col items-center gap-2">
                        <div className="w-16 h-16 rounded-full bg-white/20 border border-white/40 flex items-center justify-center backdrop-blur-md">
                          <svg width="24" height="24" viewBox="0 0 24 24" fill="white"><path d="M8 5v14l11-7z" /></svg>
                        </div>
                        <p className="text-xs font-semibold tracking-wide">{t("public_profile.video_label")}</p>
                        {detailAsset.duration_sec && (<p className="text-[10px] text-white/70">≈ {Math.round(detailAsset.duration_sec/60)} {t("public_profile.minutes")}</p>)}
                      </div>
                      {(detailAsset.owned || detailAsset.unlocked || isSubscribed) && detailAsset.play_url && (
                        <button type="button" data-testid="profile-paid-detail-play-btn" onClick={() => setPlayingVideo(true)} aria-label={t("public_profile.play_video", { defaultValue: "Video abspielen" })} className="absolute inset-0 z-20 cursor-pointer" />
                      )}
                    </div>
                  )
                ) : (
                  <div className="text-xs text-[#999]">{t("public_profile.video_label")}{detailAsset.duration_sec ? " · " + Math.round(detailAsset.duration_sec/60) + " " + t("public_profile.minutes") : ""}</div>
                )}
              </div>
              <div className="p-4 space-y-3">
                <p className="text-lg font-bold text-[#0A0A0A]">{detailAsset.price_eur.toFixed(2)} €</p>
                {detailAsset.description && (
                  <div data-testid="profile-paid-detail-description" className="text-sm text-[#333]">
                    <RichTextView html={detailAsset.description} />
                  </div>
                )}
              </div>
            </div>
            <div className="px-4 py-3 border-t border-[#E5E5E5]">
              {(detailAsset.owned || detailAsset.unlocked || isSubscribed) ? (
                <div data-testid={`profile-paid-detail-owned-${detailAsset.asset_id}`} className="w-full h-11 rounded-full bg-emerald-50 border border-emerald-300 text-emerald-800 text-sm font-medium flex items-center justify-center gap-2">
                  <CheckCircle size={14} />
                  {t("public_profile.owned_label", { defaultValue: "Bereits im Zugriff" })}
                </div>
              ) : (
                <button type="button" data-testid={`profile-paid-detail-buy-${detailAsset.asset_id}`} onClick={() => addAssetToCart(detailAsset.asset_id)} disabled={busyId === detailAsset.asset_id} className="w-full h-11 rounded-full bg-[#0A0A0A] text-white text-sm font-medium disabled:opacity-40 hover:bg-[#222] flex items-center justify-center gap-2">
                  {busyId === detailAsset.asset_id ? <Loader2 size={14} className="animate-spin" /> : <ShoppingBag size={14} />}
                  {t("public_profile.add_to_cart", { defaultValue: "In den Warenkorb" })}
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
